import { useState, useEffect } from "react";
import { Select } from "antd";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { listProjects, updateProject } from "../../../services/projects";
import type { ProjectItem } from "../../../services/projects";
import { ensurePermission } from "../../../utils/permissions";
import RIcon from "../../../components/RIcon";

type Status = "进行中" | "已完成" | "暂停";

const statusStyle: Record<Status, { cls: string; icon: string }> = {
  "进行中": { cls: "bg-[#0EA5A0]/10 text-[#0EA5A0] border-[#0EA5A0]/30", icon: "ri-play-circle-line" },
  "已完成": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "ri-checkbox-circle-line" },
  "暂停": { cls: "bg-gray-100 text-gray-500 border-gray-200", icon: "ri-pause-circle-line" },
};

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useOutletContext<{ user: import("../../../services/auth").UserInfo | null }>();

  const [project, setProject] = useState<ProjectItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editProgress, setEditProgress] = useState(0);
  const [editStatus, setEditStatus] = useState<Status>("进行中");
  const [progressDirty, setProgressDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState<{ text: string; time: string }[]>([]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    listProjects()
      .then((projects) => {
        const found = projects.find((p) => p.id === parseInt(id, 10));
        if (!found) {
          setError("未找到该项目");
          return;
        }
        setProject(found);
        setEditProgress(found.progress);
        setEditStatus((found.status as Status) || "进行中");
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "加载项目失败";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const handleSaveProgress = async () => {
    if (!ensurePermission(user, "projects:write", showToast)) return;
    if (!project) return;
    setSaving(true);
    try {
      await updateProject(project.id, { progress: editProgress, status: editStatus });
      setProject((prev) =>
        prev ? { ...prev, progress: editProgress, status: editStatus } : prev
      );
      setProgressDirty(false);
      showToast("进度已更新");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "保存失败";
      showToast("保存失败：" + msg);
    } finally {
      setSaving(false);
    }
  };

  const progressColor =
    editProgress === 100
      ? "bg-emerald-500"
      : editProgress >= 60
      ? "bg-[#0EA5A0]"
      : editProgress >= 30
      ? "bg-amber-400"
      : "bg-red-400";

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-20 flex flex-col items-center gap-3 text-[#718096]">
        <RIcon name="loader-4-line" className="text-3xl animate-spin text-[#0EA5A0]" />
        <p className="text-sm">加载项目中…</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-4xl mx-auto py-20 flex flex-col items-center gap-3 text-[#718096]">
        <RIcon name="error-warning-line" className="text-3xl text-red-400" />
        <p className="text-sm">{error || "未找到项目"}</p>
        <Link to="/workbench/projects" className="mt-2 text-sm text-[#0EA5A0] hover:underline">
          返回项目列表
        </Link>
      </div>
    );
  }

  const currentStatus =
    (project.status as Status) in statusStyle
      ? (project.status as Status)
      : "进行中";

  const startDateDisplay = project.start_date
    ? project.start_date.slice(0, 10)
    : "未设置";

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {toast && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[60] bg-[#1A2B3C] text-white text-sm px-4 py-2.5 rounded-xl flex items-center gap-2 whitespace-nowrap">
          <RIcon name="checkbox-circle-line" className="text-[#AAFF45]" />{toast}
        </div>
      )}

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm">
        <Link to="/workbench/projects" className="text-[#718096] hover:text-[#0EA5A0] transition-colors">
          项目中心
        </Link>
        <span className="text-gray-300">/</span>
        <span className="text-[#1A2B3C] font-medium truncate max-w-xs">{project.name}</span>
      </nav>

      {/* Project Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0EA5A0]/10 flex items-center justify-center flex-shrink-0">
              <RIcon name="kanban-view-line" className="text-[#0EA5A0] text-2xl" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-[#1A2B3C] mb-1">{project.name}</h1>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-[#0EA5A0] bg-[#0EA5A0]/10 px-2 py-0.5 rounded">
                  {project.code}
                </span>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1 ${statusStyle[currentStatus]?.cls}`}
                >
                  <i className={statusStyle[currentStatus]?.icon} />
                  {project.status}
                </span>
              </div>
            </div>
          </div>
          <Link
            to="/workbench/projects"
            className="flex items-center gap-1.5 text-sm text-[#718096] hover:text-[#0EA5A0] transition-colors whitespace-nowrap"
          >
            <RIcon name="arrow-left-line" />返回列表
          </Link>
        </div>

        {project.description && (
          <p className="text-sm text-[#718096] mt-4 leading-relaxed">{project.description}</p>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100">
          {[
            { label: "负责人", value: project.leader || "未指定", icon: "ri-user-line" },
            { label: "所属部门", value: project.department || "未指定", icon: "ri-building-2-line" },
            { label: "开始日期", value: startDateDisplay, icon: "ri-calendar-line" },
            { label: "当前进度", value: `${project.progress}%`, icon: "ri-progress-line" },
          ].map(({ label, value, icon }) => (
            <div key={label}>
              <p className="text-xs text-[#718096] flex items-center gap-1 mb-1">
                <i className={`${icon} text-[#0EA5A0]`} />{label}
              </p>
              <p className="text-sm font-semibold text-[#1A2B3C]">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Progress Update Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-[#1A2B3C] flex items-center gap-2">
            <RIcon name="progress-line" className="text-[#0EA5A0]" />项目进度管理
          </h3>
          {progressDirty && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              有未保存的修改
            </span>
          )}
        </div>

        <div className="flex items-center gap-4 mb-5">
          <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full ${progressColor} rounded-full transition-all duration-500`}
              style={{ width: `${editProgress}%` }}
            />
          </div>
          <span
            className={`text-2xl font-extrabold min-w-[56px] text-right ${
              editProgress === 100 ? "text-emerald-500" : "text-[#0EA5A0]"
            }`}
          >
            {editProgress}%
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
          <div>
            <label className="text-xs text-[#718096] mb-2 block font-medium">拖动调整进度</label>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              className="w-full accent-[#0EA5A0] cursor-pointer"
              value={editProgress}
              onChange={(e) => {
                setEditProgress(Number(e.target.value));
                setProgressDirty(true);
              }}
            />
            <div className="flex justify-between text-xs text-[#718096] mt-1">
              <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
            </div>
          </div>
          <div>
            <label className="text-xs text-[#718096] mb-2 block font-medium">快速设定节点</label>
            <div className="flex flex-wrap gap-2">
              {[0, 25, 50, 75, 100].map((v) => (
                <button
                  key={v}
                  onClick={() => { setEditProgress(v); setProgressDirty(true); }}
                  className={`px-3 h-8 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    editProgress === v
                      ? "bg-[#0EA5A0] text-white"
                      : "bg-[#F4F8FB] text-[#718096] hover:bg-[#0EA5A0]/10 hover:text-[#0EA5A0]"
                  }`}
                >
                  {v}%
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div>
            <label className="text-xs text-[#718096] mb-1.5 block font-medium">更新项目状态</label>
            <Select
              style={{ width: 140 }}
              value={editStatus}
              onChange={(v) => {
                setEditStatus(v as Status);
                setProgressDirty(true);
              }}
              options={[
                { value: "进行中", label: "进行中" },
                { value: "已完成", label: "已完成" },
                { value: "暂停", label: "暂停" },
              ]}
            />
          </div>
          <button
            onClick={() => setNoteOpen(true)}
            className="flex items-center gap-1.5 px-4 h-9 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:border-[#0EA5A0] hover:text-[#0EA5A0] transition-colors cursor-pointer whitespace-nowrap mt-5"
          >
            <RIcon name="sticky-note-line" />添加备注
          </button>
          <button
            onClick={handleSaveProgress}
            disabled={!progressDirty || saving}
            className="flex items-center gap-1.5 px-4 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-40 cursor-pointer whitespace-nowrap mt-5 transition-colors"
          >
            {saving
              ? <RIcon name="loader-4-line" className="animate-spin" />
              : <RIcon name="save-line" />
            }
            {saving ? "保存中…" : "保存进度"}
          </button>
        </div>
      </div>

      {/* Notes Section */}
      {notes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-[#1A2B3C] flex items-center gap-2">
              <RIcon name="sticky-note-line" className="text-[#0EA5A0]" />进度备注
              <span className="text-xs font-normal text-[#718096]">({notes.length})</span>
            </h3>
          </div>
          <div className="divide-y divide-gray-50">
            {notes.map((n, i) => (
              <div key={i} className="px-6 py-3 flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#0EA5A0]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <RIcon name="chat-3-line" className="text-[#0EA5A0] text-xs" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#4A5568] leading-relaxed whitespace-pre-wrap">{n.text}</p>
                  <p className="text-xs text-[#718096] mt-1">{n.time}</p>
                </div>
                <button
                  onClick={() => setNotes((prev) => prev.filter((_, idx) => idx !== i))}
                  className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-400 cursor-pointer flex-shrink-0"
                  title="删除备注"
                >
                  <RIcon name="close-line" className="text-xs" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Note Modal */}
      {noteOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[#1A2B3C]">添加进度备注</h3>
              <button
                onClick={() => setNoteOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
              >
                <RIcon name="close-line" className="text-[#718096]" />
              </button>
            </div>
            <textarea
              rows={4}
              placeholder="记录本次进度更新的具体内容、完成情况或遇到的问题…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#0EA5A0] resize-none"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
            <p className="text-xs text-[#718096] text-right mt-1">{note.length}/500</p>
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => setNoteOpen(false)}
                className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => {
                  if (note.trim()) {
                    setNotes((prev) => [{ text: note.trim(), time: new Date().toLocaleString("zh-CN") }, ...prev]);
                    showToast("备注已保存");
                  }
                  setNoteOpen(false);
                  setNote("");
                }}
                disabled={!note.trim()}
                className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                保存备注
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
